import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon } from '@radix-ui/react-icons';
import { Button, Card, Flex, Spinner } from '@radix-ui/themes';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import RadioGroupComponent from '../../components/inputs/RadioGroup/RadioGroupComponent';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import TextFieldComponent from '../../components/inputs/Textfield/TextfieldComponent';
import { useGetScientificAreasQuery } from '../../hooks/useGetScientificAreasQuery';
import { useGetSubjectsQuery } from '../../hooks/useGetSubjectsQuery';
import { resetUpdateSubjectData } from '../../redux/slices/school/subject';
import { RootState } from '../../redux/store';
import { routes } from '../../routes';
import { updateSubject } from '../../services/school/subject/updateSubject';
import { UpdateSubjectType } from '../../types/Subject';
import { UpdateSubjectFormValidation } from './UpdateSubjectFormValidation';

export const UpdateSubject = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const subjectData = useSelector((state: RootState) => state.subject);
  const defaultValues: UpdateSubjectType = subjectData;
  const { control, handleSubmit, setValue, getValues, formState: { errors } } = useForm<UpdateSubjectType>({
    resolver: yupResolver(UpdateSubjectFormValidation),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });
  const mutation = useMutation({ mutationFn: updateSubject, mutationKey: 'updateSubject' });
  const { data: scientificAreas, isLoading: isLoadingScientificAreas, isError: errorScientificAreas } = useGetScientificAreasQuery({ PageNumber: 1, PageSize: 400 });
  const { data: subjects, isLoading: isLoadingSubjects, isError: errorSubjects, refetch: refreshSubjects } = useGetSubjectsQuery({ PageNumber: 1, PageSize: 400, idScientificArea: getValues('idScientificArea') },
    { enabled: !!getValues('idScientificArea') });

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!data.idPrecedence) {
        data = { ...data, idPrecedence: undefined };
      }
      const result = await mutation.mutateAsync({ ...data, id: subjectData.id });
      if (result) {
        toast.success("Disciplina actualizada");
        dispatch(resetUpdateSubjectData());
        navigate(routes.subjects);
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const handleBack = () => {
    dispatch(resetUpdateSubjectData());
    navigate(routes.subjects);
  };
  const handleScientificAreaChange = (idScientificArea: string) => {
    setValue('idScientificArea', idScientificArea);
    refreshSubjects();
  };
  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size='1'>
        <Flex direction='column' gap='4'>

          <TextFieldComponent name="description" label='Descrição' control={control} errors={errors} fieldProps={{ placeholder: 'Descrição' }} required={true} />
          <SelectComponent name="idScientificArea" label='Área Científica' control={control} errors={errors} style={{ width: '100%' }}
            options={scientificAreas?.items.map(scientificArea => ({
              value: scientificArea.id ?? "",
              label: scientificArea.description ?? ""
            }))}
            onSelect={handleScientificAreaChange}
            required={true}
          />

          <SelectComponent name="idPrecedence" label="Precedência" control={control} errors={errors} style={{ width: '100%' }}
            options={subjects?.items.map(subject => ({
              value: subject.id ?? "",
              label: subject.description ?? ""
            }))}
            onSelect={handleScientificAreaChange}
          />

          <RadioGroupComponent name="isCore" label="Tipo de disciplina" control={control} errors={errors} style={{ width: '100%' }}
            options={[
              { value: "true", label: "Nuclear" }
              , { value: "false", label: "Opcional" }]}
            fieldProps={{
              style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
            }}
          />

          <RadioGroupComponent name="hasExam" label="Tem exame?" control={control} errors={errors} style={{ width: '100%' }}
            options={[
              { value: "true", label: "Sim" }
              , { value: "false", label: "Não" }]}
            fieldProps={{
              style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
            }}
          />
          <RadioGroupComponent name="isMonograph" label='É Monografia' control={control} errors={errors} style={{ width: '100%' }}
            options={[
              { value: "true", label: "Sim" }
              , { value: "false", label: "Não" }]}
            fieldProps={{
              style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
            }}
            required={true}
          />

          <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
            <Button type="button" variant="soft" color="gray" onClick={handleBack} disabled={mutation.isLoading}>
              <ChevronLeftIcon /> Voltar
            </Button>
            <Button type="submit" disabled={mutation.isLoading} variant='surface'>
              {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
              Registar
            </Button>
          </Flex>
        </Flex>
      </Card>
    </form>
  );
}